import { Expense } from '@/utils/api';
import { ReceiptScanResult } from '@/utils/api';

/**
 * Maps ReceiptScanResult fields to a partial Expense object
 * that can be passed directly to RefuelEditDrawer or ExpenseEditDrawer
 * as the `refuel` or `expense` prop.
 *
 * Note: The resulting object intentionally has NO `id` field,
 * so the drawer stays in create mode (isEditMode = Boolean(refuel?.id) = false).
 */
export const mapScanResultToExpense = (result: ReceiptScanResult): Partial<Expense> => {
  const { fields, extra } = result;

  const expense: Partial<Expense> = {
    // No id — keeps drawer in create mode
    expenseType: result.expenseType,
  };

  if (result.uploadedFiles && result.uploadedFiles.length) {
    expense.uploadedFiles = result.uploadedFiles;
  }

  // -- Common fields --
  if (fields.whenDone) {
    expense.whenDone = fields.whenDone;
  }
  if (fields.odometer != null) {
    expense.odometer = fields.odometer;
  }
  if (fields.location) {
    expense.location = fields.location;
  }
  if (fields.whereDone) {
    expense.whereDone = fields.whereDone;
  }
  if (fields.subtotal != null) {
    // expense.subtotal is not visible to user in the expense drawer,
    // so we map it to costWork as a workaround to preserve the value.
    expense.costWork = fields.subtotal;
  }
  if (fields.tax != null) {
    expense.tax = fields.tax;
  }
  if (fields.fees != null) {
    expense.fees = fields.fees;
  }
  if (fields.totalPrice != null) {
    expense.totalPrice = fields.totalPrice;
  }
  if (fields.paidInCurrency) {
    expense.paidInCurrency = fields.paidInCurrency;
  }
  if (fields.comments) {
    expense.comments = fields.comments;
  }

  // -- Address fields --
  if (fields.address1) {
    expense.address1 = fields.address1;
  }
  if (fields.address2) {
    expense.address2 = fields.address2;
  }
  if (fields.city) {
    expense.city = fields.city;
  }
  if (fields.postalCode) {
    expense.postalCode = fields.postalCode;
  }
  if (fields.stateProvince) {
    expense.stateProvince = fields.stateProvince;
  }
  if (fields.country) {
    expense.country = fields.country;
  }
  if (fields.countryId) {
    expense.countryId = fields.countryId;
  }

  // -- Refuel-specific fields (expenseType=1) --
  if (result.expenseType === 1) {
    if (fields.refuelVolume != null) {
      expense.refuelVolume = fields.refuelVolume;
    }
    if (fields.volumeEnteredIn) {
      expense.volumeEnteredIn = fields.volumeEnteredIn;
    }
    if (fields.pricePerVolume != null) {
      expense.pricePerVolume = fields.pricePerVolume;
    }
    if (fields.fuelGrade) {
      expense.fuelGrade = fields.fuelGrade;
    }
  }

  // -- Expense-specific fields (expenseType=2) --
  if (result.expenseType === 2) {
    if (fields.kindId != null) {
      expense.kindId = fields.kindId;
    }
    if (fields.costWork != null) {
      expense.costWork = fields.costWork;
    }
    if (fields.costParts != null) {
      expense.costParts = fields.costParts;
    }
    if (fields.shortNote) {
      expense.shortNote = fields.shortNote;
    }

    // Fallback: derive costWork/costParts from line items when fields don't have them.
    // Claude sometimes puts the breakdown only in extra.lineItems with typed entries
    // (type: 'labor' | 'part') while leaving fields.costWork/costParts empty.
    if (extra?.lineItems?.length) {
      if (expense.costWork == null) {
        const laborTotal = extra.lineItems
          .filter((li) => li.type === 'labor' && li.amount != null)
          .reduce((sum, li) => sum + li.amount!, 0);
        if (laborTotal > 0) {
          expense.costWork = laborTotal;
        }
      }
      if (expense.costParts == null) {
        const partsTotal = extra.lineItems
          .filter((li) => li.type === 'part' && li.amount != null)
          .reduce((sum, li) => sum + li.amount!, 0);
        if (partsTotal > 0) {
          expense.costParts = partsTotal;
        }
      }
    }
  }

  // -- Build comments from extra data if no comments from fields --
  if (!expense.comments && extra) {
    const commentParts: string[] = [];

    // Receipt / payment info
    if (extra.receiptNumber) {
      commentParts.push(`Receipt #${extra.receiptNumber}`);
    }
    if (extra.paymentMethod) {
      commentParts.push(
        `Paid: ${extra.paymentMethod}${extra.cardLastFour ? ` ****${extra.cardLastFour}` : ''}`
      );
    }
    if (extra.workOrderNumber) {
      commentParts.push(`Work order #${extra.workOrderNumber}`);
    }
    if (extra.pumpNumber) {
      commentParts.push(`Pump #${extra.pumpNumber}`);
    }
    if (extra.staffName) {
      commentParts.push(`Staff: ${extra.staffName}`);
    }
    if (extra.tipAmount != null) {
      commentParts.push(`Tip: ${extra.tipAmount}`);
    }

    // Parking details
    if (extra.parkingDuration) {
      commentParts.push(`Duration: ${extra.parkingDuration}`);
    }
    if (extra.parkingSpotOrZone) {
      commentParts.push(`Zone: ${extra.parkingSpotOrZone}`);
    }
    if (extra.parkingEntryDateTime) {
      commentParts.push(`Entry: ${extra.parkingEntryDateTime}`);
    }
    if (extra.parkingExitDateTime) {
      commentParts.push(`Exit: ${extra.parkingExitDateTime}`);
    }

    // Fine / ticket details
    if (extra.ticketNumber) {
      commentParts.push(`Ticket #${extra.ticketNumber}`);
    }
    if (extra.violationType) {
      commentParts.push(`Violation: ${extra.violationType}`);
    }
    if (extra.violationLocation) {
      commentParts.push(`At: ${extra.violationLocation}`);
    }
    if (extra.fineDueDate) {
      commentParts.push(`Due: ${extra.fineDueDate}`);
    }

    // Insurance details
    if (extra.policyNumber) {
      commentParts.push(`Policy #${extra.policyNumber}`);
    }
    if (extra.coveragePeriodStart && extra.coveragePeriodEnd) {
      commentParts.push(`Coverage: ${extra.coveragePeriodStart} – ${extra.coveragePeriodEnd}`);
    }

    // Toll details
    if (extra.tollRoadName) {
      commentParts.push(`Toll: ${extra.tollRoadName}`);
    }
    if (extra.tollEntryPoint && extra.tollExitPoint) {
      commentParts.push(`${extra.tollEntryPoint} → ${extra.tollExitPoint}`);
    }

    // Vehicle info (useful when user has multiple vehicles)
    if (extra.licensePlate) {
      commentParts.push(`Plate: ${extra.licensePlate}`);
    }

    // Line items summary for reference
    if (extra.lineItems?.length) {
      const summary = extra.lineItems
        .map((li) => (li.amount != null ? `${li.description}: ${li.amount}` : li.description))
        .join('; ');
      commentParts.push(`Items: ${summary}`);
    }

    if (commentParts.length > 0) {
      expense.comments = commentParts.join(' · ');
    }
  }

  return expense;
};